import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { useQueries } from '@tanstack/react-query';
import {
	addMonths,
	endOfMonth,
	format,
	formatISO,
	isAfter,
	isBefore,
	isFuture,
	isPast,
	isSameMonth,
	isThisMonth,
	isThisYear,
	parseISO,
	startOfMonth,
	subMonths,
} from 'date-fns';

import * as GraphStore from '@asteria/datalayer/stores/graph';

import { useTranslate } from '@asteria/language';
import { getLabels } from '@asteria/utils-funcs/graph';

import { filterOperations, useTransactions } from '../../../utils/operations';

export function useLineGraphData(options) {
	const { dates, absolute, onSubmit } = options;
	const { t } = useTranslate();
	const afterDate = addMonths(dates[0], -1);
	let startDate = afterDate;
	const endDate = addMonths(dates[dates.length - 1], 1);

	if (
		isAfter(startDate, startOfMonth(new Date())) ||
		isSameMonth(startDate, new Date())
	) {
		startDate = startOfMonth(addMonths(new Date(), -1));
	}

	const [history] = useQueries({
		queries: [
			{
				queryKey: ['accountbalance', startDate, endDate],
				queryFn: async () => {
					return onSubmit?.('accountbalance:fetch', {
						range: 'MONTH',
						fromDate: format(startDate, 'yyyy-MM-01'),
						toDate: format(endDate, 'yyyy-MM-dd'),
						fields: `
							date
							balanceAtEndOfPeriod
							balanceAtStartOfPeriod
							deposits
							withdrawals
						`,
					});
				},

				refetchOnMount: true,
				refetchOnReconnect: false,
				refetchOnWindowFocus: false,
			},
		],
	});

	const operations = useTransactions({});

	// Remap history to match the format of the lineValues array
	let lineValues = (history?.data ?? []).map(
		({
			date,
			balanceAtEndOfPeriod,
			balanceAtStartOfPeriod,
			deposits,
			withdrawals,
		}) => {
			return {
				date: date,
				value: balanceAtEndOfPeriod,
				valueStart: balanceAtStartOfPeriod,
				min: balanceAtEndOfPeriod,
				max: balanceAtEndOfPeriod,
				deposit: { value: deposits },
				withdraw: { value: withdrawals },
			};
		},
	);

	// Apply forecast values
	for (const [index, value] of lineValues.entries()) {
		const date = parseISO(value.date);
		if (isThisMonth(date) || isFuture(date)) {
			// calculate forecast values, based on operations

			const activeOperations = filterOperations(operations, {
				startDate: formatISO(date, { representation: 'date' }),
				endDate: formatISO(endOfMonth(date), {
					representation: 'date',
				}),
			});

			const startValue =
				index === 0
					? lineValues[index].valueStart
					: lineValues[index - 1].value;

			lineValues[index] = activeOperations.reduce(
				(acc, { payload: { total, type } }) => {
					acc.value =
						acc.value + (type === 'DEPOSIT' ? total : -total);
					acc.deposit.value =
						acc.deposit.value + (type === 'DEPOSIT' ? total : 0);
					acc.withdraw.value =
						acc.withdraw.value + (type === 'WITHDRAW' ? total : 0);
					acc.max = acc.value + Math.abs(acc.value) * 0.2;
					acc.min = acc.value - Math.abs(acc.value) * 0.2;
					return acc;
				},
				{
					// If first value, use the value from active month, otherwise use the previous value
					value: startValue,
					valueStart: startValue,
					deposit: { value: 0 },
					withdraw: { value: 0 },
					max: startValue + Math.abs(startValue) * 0.2,
					min: startValue - Math.abs(startValue) * 0.2,
					date: value.date,
				},
			);
		}
	}

	lineValues = lineValues.filter(
		({ date }) => !isBefore(parseISO(date), afterDate),
	);

	const max = Math.max(
		...lineValues.flatMap(({ value, min, max }) => [value, min, max]),
	);

	const min = Math.min(
		...lineValues.flatMap(({ value, min, max }) => [value, min, max]),
	);

	const scale = useMemo(() => {
		const absolute = Math.max(Math.abs(max), Math.abs(min));

		if (!absolute) {
			return 0;
		}

		return Math.floor(Math.floor(Math.log10(absolute)) / 3) - 1;
	}, [max, min]);

	const lineLabels = useMemo(() => {
		let $min = min,
			$max = max;

		if (absolute) {
			$max = Math.max(Math.abs(min), Math.abs(max));
			$min = -$max;
		}

		if (Math.abs($max) <= 1 && Math.abs($min) <= 1) {
			$max = 10_000;
			$min = -$max;
		}

		const labels = getLabels({
			max: $max,
			min: $min,
			scale: scale,
			steps: 4,
			prefix: false,
		});

		return labels.reverse().map((object, index) => ({
			...object,
			size: index ? 'medium' : undefined,
		}));
	}, [absolute, max, min, scale]);

	const lineLabelsMax = Math.max(
		1_000,
		...lineLabels.map(({ value }) => value),
	);

	const lineLabelsMin = Math.min(0, ...lineLabels.map(({ value }) => value));

	const response = {
		labels: lineLabels,
		max: lineLabelsMax,
		min: lineLabelsMin,
		data: lineValues,
		title: t(
			[
				'yaxis.label',
				`yaxis.label.SEK`,
				`yaxis.label.${scale}`,
				`yaxis.label.SEK.${scale}`,
			],
			{ default: 'TKr' },
		),
	};

	return response;
}

export function useLineType($date, type = 'month') {
	if (!$date) {
		return 'future';
	}

	const date = typeof $date === 'string' ? parseISO($date) : $date;

	if (type === 'month' && isThisMonth(date)) {
		return 'current';
	}

	if (type === 'year' && isThisYear(date)) {
		return 'current';
	}

	if (isPast(date)) {
		return 'history';
	}

	return 'future';
}

export function useLegends({ dates }) {
	const hasPast = dates.some((date) => isPast(date) && !isThisMonth(date));
	const hasFuture = dates.some((date) => isFuture(date) || isThisMonth(date));
	const hasSpread = dates.some((date) =>
		isSameMonth(date, subMonths(new Date(), 1)),
	);

	const { t } = useTranslate();

	return useMemo(
		() =>
			[
				hasPast
					? {
						type: ['account', 'paid'],
						title: t('graph.account.legend.history'),
					}
					: null,
				hasFuture
					? {
						type: ['account', 'forecast'],
						title: t('graph.account.legend.forecast'),
					}
					: null,
				hasFuture || hasSpread
					? {
						type: ['spread'],
						title: t('graph.account.legend.spread'),
					}
					: null,
			].filter(Boolean),
		[hasFuture, hasPast, hasSpread, t],
	);
}

export function useStateHidden() {
	const selected = useSelector(GraphStore.selectors.legends.selected);
	const exists = !!selected.length;

	const spread = selected.some((object) => object?.id === 'spread');
	const lineHistory = selected.some(
		(object) => object?.id === 'account-paid',
	);
	const lineFuture = selected.some(
		(object) => object?.id === 'account-forecast',
	);

	return useMemo(() => {
		if (!exists) {
			return;
		}

		return {
			spread: !spread,
			line: { history: !lineHistory, future: !lineFuture },
		};
	}, [exists, lineFuture, lineHistory, spread]);
}

export function useStateInvisible() {
	const highlight = useSelector(GraphStore.selectors.legends.highlight);
	const exists = !!highlight;

	const spread = highlight?.id === 'spread';
	const lineHistory = highlight?.id === 'account-paid';
	const lineFuture = highlight?.id === 'account-forecast';

	return useMemo(() => {
		if (!exists) {
			return;
		}

		return {
			spread: !spread,
			line: { history: !lineHistory, future: !lineFuture },
		};
	}, [exists, lineFuture, lineHistory, spread]);
}
